import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Icon } from '@ascend/shared/components';
import {
  DataTable,
  SearchBar,
  PaginationControls,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../components/TableControls';
import { getPoolDetail } from '../data/mockAdapter';

// ─── Types ───────────────────────────────────────────────────────────────────

type PoolDetail = NonNullable<Awaited<ReturnType<typeof getPoolDetail>>>;
type LenderRow = PoolDetail['lenders'][number];
type LenderSortKey = 'name' | 'depositedUsd' | 'earnedYieldUsd' | 'sharePct' | 'withdrawablePct' | 'iouShareUsd';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function truncateAddress(addr: string): string {
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const IOU_STATUS_CLASS: Record<string, string> = {
  outstanding: 'bg-red-100 text-red-700',
  partially_repaid: 'bg-yellow-100 text-yellow-700',
  settled: 'bg-green-100 text-green-700',
};

// ─── StatCard ────────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon }: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}

// ─── Page Component ──────────────────────────────────────────────────────────

export function StablecoinPoolDetailPage() {
  const { poolId } = useParams<{ poolId: string }>();
  const [detail, setDetail] = useState<PoolDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Lender table state
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);
  const [sortKey, setSortKey] = useState<LenderSortKey>('depositedUsd');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (!poolId) return;
    let cancelled = false;
    getPoolDetail(poolId).then((data) => {
      if (cancelled) return;
      setDetail(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [poolId]);

  function toggleSort(key: LenderSortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  }

  const filteredLenders = useMemo(() => {
    if (!detail) return [];
    let result = [...detail.lenders];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) => l.name.toLowerCase().includes(q) || l.wallet.toLowerCase().includes(q) || l.userId.toLowerCase().includes(q),
      );
    }
    result.sort((a, b) => {
      const cmp = (a[sortKey] as number) - (b[sortKey] as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [detail, search, sortKey, sortDir]);

  const paginatedLenders = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filteredLenders.length / pageSize));
    const safePage = Math.min(page, totalPages);
    return filteredLenders.slice((safePage - 1) * pageSize, safePage * pageSize);
  }, [filteredLenders, page, pageSize]);

  const lenderColumns = useMemo(
    () => [
      {
        id: 'wallet',
        label: 'Wallet',
        renderCell: (l: LenderRow) => (
          <div>
            <span className="font-mono text-xs">{truncateAddress(l.wallet)}</span>
            <div className="text-xs text-muted-foreground">{l.name}</div>
          </div>
        ),
      },
      {
        id: 'depositedUsd',
        label: 'Deposited',
        align: 'right' as const,
        sortable: true,
        sortKey: 'depositedUsd' as const,
        renderCell: (l: LenderRow) => fmtCurrency(l.depositedUsd),
      },
      {
        id: 'sharePct',
        label: 'Pool Share',
        align: 'right' as const,
        sortable: true,
        sortKey: 'sharePct' as const,
        renderCell: (l: LenderRow) => `${l.sharePct.toFixed(1)}%`,
      },
      {
        id: 'lpTokens',
        label: 'LP Tokens',
        align: 'right' as const,
        renderCell: (l: LenderRow) => l.lpTokens.toLocaleString(),
      },
      {
        id: 'earnedYieldUsd',
        label: 'Yield Earned',
        align: 'right' as const,
        sortable: true,
        sortKey: 'earnedYieldUsd' as const,
        renderCell: (l: LenderRow) => fmtCurrency(l.earnedYieldUsd),
      },
      {
        id: 'withdrawablePct',
        label: 'Withdrawable',
        align: 'right' as const,
        sortable: true,
        sortKey: 'withdrawablePct' as const,
        renderCell: (l: LenderRow) => (
          <span className={l.withdrawablePct < 100 ? 'text-yellow-600' : 'text-green-600'}>
            {l.withdrawablePct}%
          </span>
        ),
      },
      {
        id: 'iouShareUsd',
        label: 'IOU Share',
        align: 'right' as const,
        sortable: true,
        sortKey: 'iouShareUsd' as const,
        renderCell: (l: LenderRow) =>
          l.iouShareUsd > 0 ? (
            <span className="text-yellow-600">{fmtCurrency(l.iouShareUsd)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
    ],
    [],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-muted-foreground">Loading pool details...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-6">
        <Link to="/stablecoin-pools" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" /> Back to Pools
        </Link>
        <div className="text-center py-20 text-muted-foreground">Pool not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Back link */}
      <Link to="/stablecoin-pools" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" /> Back to Pools
      </Link>

      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:bank" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{detail.name}</h1>
            <p className="text-sm text-muted-foreground">
              Linked to{' '}
              <Link to={`/cf/${encodeURIComponent(detail.facilityName)}`} className="text-primary hover:underline">
                {detail.facilityName}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total TVL"
          value={fmtCurrency(detail.tvlUsd)}
          subtitle={`${detail.lenderCount} lenders`}
          icon={<Icon name="streamline:money-cash-coins-stack-accounting-billing-payment-stack-cash-coins-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Deposited"
          value={fmtCurrency(detail.totalDeposited)}
          subtitle="All lender deposits"
          icon={<Icon name="streamline:piggy-bank" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Total Yield Paid"
          value={fmtCurrency(detail.totalEarnedYield)}
          subtitle="Interest paid to lenders"
          icon={<Icon name="streamline:heart-rate-pulse-graph" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="APY / Utilization"
          value={`${detail.apyPct.toFixed(1)}% / ${detail.utilizationPct.toFixed(1)}%`}
          subtitle="Current rates"
          icon={<Icon name="streamline:interface-user-multiple-close-geometric-human-multiple-person-up-user" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Lenders Table */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="text-lg font-semibold">Lender Positions</h2>
          <SearchBar
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Search by wallet or name"
            className="w-full sm:w-80"
          />
        </div>

        <DataTable<LenderRow, LenderSortKey>
          columns={lenderColumns}
          rows={paginatedLenders}
          rowKey={(row) => row.userId}
          sort={{
            key: sortKey,
            direction: sortDir,
            onSort: toggleSort,
          }}
          emptyMessage="No lender positions found."
        />

        <PaginationControls
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={filteredLenders.length}
          label="lenders"
          pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
        />
      </div>

      {/* IOUs Section */}
      {detail.ious.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Icon name="streamline:interface-file-text-text-common-file" className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">IOUs</h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {detail.ious.map((iou) => (
              <div key={iou.id} className="rounded-xl border border-border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-muted-foreground">{iou.id}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${IOU_STATUS_CLASS[iou.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {iou.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">{iou.facilityName}</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xs text-muted-foreground">Original</div>
                    <div className="text-sm font-bold">{fmtCurrency(iou.originalAmountUsd)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Repaid</div>
                    <div className="text-sm font-bold text-green-600">{fmtCurrency(iou.repaidAmountUsd)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Remaining</div>
                    <div className="text-sm font-bold text-red-600">{fmtCurrency(iou.remainingAmountUsd)}</div>
                  </div>
                </div>
                {iou.events.length > 0 && (
                  <div className="border-t border-border pt-2 space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">Events</div>
                    {iou.events.map((event) => (
                      <div key={event.id} className="flex items-center justify-between text-xs">
                        <span className={`rounded px-1.5 py-0.5 font-medium ${event.eventType === 'mint' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {event.eventType.toUpperCase()}
                        </span>
                        <span>{fmtCurrency(event.amountUsd)}</span>
                        <span className="text-muted-foreground">{event.createdAt}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
